import { useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { Icon } from "../common/Icon";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { LinkGen, QueryKeys } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import "./PageMetaAdsOAuthResult.css";

type OAuthStatus = "success" | "error";
type OAuthReason =
	| "denied"
	| "missing_code"
	| "oauth_error"
	| "exchange_failed"
	| string;

function getErrorCopy(reason: OAuthReason | null, detail: string | null) {
	switch (reason) {
		case "denied":
			return {
				title: "Meta authorization was declined",
				summary:
					"You cancelled the Meta login or denied the requested permissions. Without these permissions we cannot manage ads for this client.",
				steps: [
					"Click Try again and sign in with a Meta user that has access to the client’s Business Manager.",
					"When prompted, allow ads_management, ads_read, business_management, and pages permissions.",
					"If you use Facebook Login restrictions, ask a Business Admin to approve this app.",
				],
				detail:
					detail ||
					"Authorization was denied before a connection could be created.",
			};
		case "missing_code":
			return {
				title: "Meta did not return an authorization code",
				summary:
					"The OAuth redirect completed without a usable code, so we could not finish connecting this account.",
				steps: [
					"Try connecting again from this page.",
					"Disable browser extensions that block redirects, then retry.",
					"If it still fails, reconnect from Settings → Apps and contact support with the time of the attempt.",
				],
				detail: detail || "No authorization code was provided by Meta.",
			};
		case "oauth_error":
			return {
				title: "Meta returned an authorization error",
				summary:
					"Meta rejected the authorization request before we could link the ad account.",
				steps: [
					"Confirm the Meta app is live (or that your user is a test user/admin).",
					"Try again with a Business Admin account for the client.",
					"If the error mentions permissions, ensure Ads and Pages access are granted.",
				],
				detail: detail || "Meta returned an OAuth error.",
			};
		default:
			return {
				title: "Could not connect Meta Ads",
				summary:
					"Something went wrong while exchanging the authorization code for an access token.",
				steps: [
					"Click Try again to restart the Meta OAuth flow.",
					"Make sure you grant all requested permissions.",
					"If the problem continues, disconnect any partial connection in Settings → Apps and retry.",
				],
				detail:
					detail ||
					"We could not complete the Meta Ads connection. Please try again.",
			};
	}
}

export const PageMetaAdsOAuthResult: FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const [retrying, setRetrying] = useState(false);

	const status = (searchParams.get("status") as OAuthStatus | null) ?? "error";
	const reason = searchParams.get("reason") as OAuthReason | null;
	const detail = searchParams.get("detail");
	const isSuccess = status === "success";

	useEffect(() => {
		if (!isSuccess) return;
		void queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.Status(company.id),
		});
		void queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.AdAccountSingle(company.id),
		});
		void queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.PageSingle(company.id),
		});
		void queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.Overview(),
		});
	}, [isSuccess, company.id, queryClient]);

	const errorCopy = getErrorCopy(reason, detail);

	const handleRetry = async () => {
		setRetrying(true);
		try {
			const link = await Api.MetaAdsConnect().then((res) => res.data);
			window.location.href = link;
		} catch {
			setRetrying(false);
			emit({
				type: AlertType.ERROR,
				content:
					"Could not start Meta Ads authorization. Check you have Manager access, then try again.",
			});
		}
	};

	return (
		<AppPage
			title={isSuccess ? "Meta Ads connected" : "Meta Ads connection"}
			subtitle={
				isSuccess
					? "Your client’s Meta ad account is ready."
					: "Review what went wrong and retry when ready."
			}
		>
			<div className="meta-ads-oauth-result">
				<div className="meta-ads-oauth-result__card">
					{isSuccess ? (
						<>
							<div className="meta-ads-oauth-result__icon meta-ads-oauth-result__icon--success">
								<Icon name="check_circle" size={32} />
							</div>
							<h2 className="meta-ads-oauth-result__title">
								Meta Ads connected successfully
							</h2>
							<p className="meta-ads-oauth-result__summary">
								{company.name} is linked to Meta. Confirm the Ad Account and
								Facebook Page in Settings, then start managing campaigns.
							</p>
							<ol className="meta-ads-oauth-result__steps">
								<li>
									Verify the selected Ad Account and Facebook Page under
									Settings → Apps.
								</li>
								<li>Open Meta Ads to create campaigns or review reporting.</li>
							</ol>
							<div className="meta-ads-oauth-result__actions">
								<Button onClick={() => navigate(LinkGen.MetaAds.Index())}>
									Go to Meta Ads
								</Button>
								<Button
									variant="secondary"
									onClick={() => navigate(LinkGen.Settings.Apps())}
								>
									Review connection settings
								</Button>
							</div>
						</>
					) : (
						<>
							<div className="meta-ads-oauth-result__icon meta-ads-oauth-result__icon--error">
								<Icon name="error" size={32} />
							</div>
							<h2 className="meta-ads-oauth-result__title">
								{errorCopy.title}
							</h2>
							<p className="meta-ads-oauth-result__summary">
								{errorCopy.summary}
							</p>
							<div className="meta-ads-oauth-result__detail">
								<strong>Details:</strong> {errorCopy.detail}
							</div>
							<p style={{ fontWeight: 600, marginBottom: 8 }}>
								How to fix this
							</p>
							<ol className="meta-ads-oauth-result__steps">
								{errorCopy.steps.map((step) => (
									<li key={step}>{step}</li>
								))}
							</ol>
							<div className="meta-ads-oauth-result__actions">
								<Button loading={retrying} onClick={handleRetry}>
									Try again
								</Button>
								<Button
									variant="secondary"
									onClick={() => navigate(LinkGen.Settings.Apps())}
								>
									Back to Settings → Apps
								</Button>
								<Button
									variant="ghost"
									onClick={() => navigate(LinkGen.MetaAds.Index())}
								>
									Meta Ads home
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</AppPage>
	);
};
